<template>
  <div class="travel-page">
    <!-- Hero -->
    <section class="travel-hero">
      <div class="travel-hero-inner">
        <div class="travel-eyebrow" data-aos="fade-up">
          <span class="dot"></span>Getting to the Summit
        </div>
        <h1 class="travel-title" data-aos="fade-up" data-aos-delay="100">
          Travel &amp; <span class="accent">transfers</span> to Arusha.
        </h1>
        <p class="travel-lede" data-aos="fade-up" data-aos-delay="200">
          How to reach Mount Meru Hotel, Arusha, for the 2<sup>nd</sup> Africa
          Human Resource Summit &mdash; 18&ndash;22 August 2026. Flights,
          airport transfers, and getting around, with a trusted local partner
          on hand.
        </p>
      </div>
    </section>

    <!-- Content -->
    <section class="travel-content">
      <div class="travel-wrap">

        <!-- Transfer partner (primary) -->
        <div class="travel-card partner-card" data-aos="fade-up">
          <div class="partner-badge">Official travel partner</div>
          <h2 class="card-title">Airport transfers &amp; tours</h2>
          <p class="card-text">
            We&rsquo;ve partnered with <strong>Skylink Tanzania</strong> to
            arrange your airport pick-up, hotel transfers, and the Summit
            heritage tour. Request your transfer through their contact form and
            mention <strong>&ldquo;AHRS 2026&rdquo;</strong> with your arrival
            flight and date.
          </p>
          <a
            href="https://skylinktanzania.co.tz/get-in-touch/#contact-form"
            target="_blank"
            rel="noopener noreferrer"
            class="travel-btn"
          >
            Arrange my transfer
            <i class="fas fa-arrow-right"></i>
          </a>
          <p class="card-note">skylinktanzania.co.tz</p>
        </div>

        <!-- Two column grid -->
        <div class="travel-grid">
          <div class="travel-card" data-aos="fade-up">
            <div class="card-icon"><i class="fas fa-plane-arrival"></i></div>
            <h3 class="card-subtitle">Fly into Kilimanjaro (JRO)</h3>
            <p class="card-text">
              <strong>Kilimanjaro International Airport (JRO)</strong> is the
              main gateway, about <strong>46 km (1 hour)</strong> from Arusha.
              It is served by major regional and international carriers. Arusha
              Airport (ARK) handles domestic and light-aircraft flights closer
              to town.
            </p>
          </div>

          <div class="travel-card" data-aos="fade-up" data-aos-delay="100">
            <div class="card-icon"><i class="fas fa-hotel"></i></div>
            <h3 class="card-subtitle">The venue</h3>
            <p class="card-text">
              All Summit sessions take place at
              <strong>Mount Meru Hotel, Arusha</strong>. Registration and badge
              collection open on <strong>Tuesday, 18 August</strong> at the
              hotel reception desk. Transfers from JRO drop you directly at the
              venue.
            </p>
          </div>

          <div class="travel-card" data-aos="fade-up">
            <div class="card-icon"><i class="fas fa-route"></i></div>
            <h3 class="card-subtitle">Getting around Arusha</h3>
            <p class="card-text">
              Arusha is compact and walkable around the town centre. For wider
              trips use hotel-arranged cars or your Skylink driver rather than
              hailing on the street. Allow extra time during peak hours.
            </p>
          </div>

          <div class="travel-card" data-aos="fade-up" data-aos-delay="100">
            <div class="card-icon"><i class="fas fa-binoculars"></i></div>
            <h3 class="card-subtitle">Heritage tour transfer</h3>
            <p class="card-text">
              The <strong>Tarangire National Park</strong> heritage tour on
              <strong>Saturday, 22 August</strong> includes coordinated road
              transfers from Arusha. Book the tour with your registration and
              confirm pick-up through Skylink.
            </p>
          </div>
        </div>

        <!-- Practical strip -->
        <div class="travel-card practical-card" data-aos="fade-up">
          <h2 class="card-title">Good to know</h2>
          <ul class="practical-list">
            <li><i class="fas fa-clock"></i> Tanzania is on <strong>EAT (UTC+3)</strong>, year-round.</li>
            <li><i class="fas fa-money-bill-wave"></i> Currency is the <strong>Tanzanian Shilling (TZS)</strong>; USD is widely accepted at hotels.</li>
            <li><i class="fas fa-cloud-sun"></i> August in Arusha is cool and dry &mdash; pack a light jacket for evenings.</li>
            <li><i class="fas fa-plug"></i> Power is <strong>230V, Type D/G</strong> sockets &mdash; bring a UK-style adapter.</li>
          </ul>
        </div>

        <!-- Visa cross-link -->
        <div class="travel-cta" data-aos="zoom-in">
          <h2 class="cta-title">Need an entry visa?</h2>
          <p class="cta-subtitle">
            Check Tanzania&rsquo;s visa and entry requirements before you book.
          </p>
          <router-link to="/visa" class="cta-btn">
            Visa &amp; Entry <i class="fas fa-arrow-right"></i>
          </router-link>
        </div>

      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "TravelView",
};
</script>

<style scoped>
/* FCM palette */
.travel-page {
  --fcm-green: #00de00;
  --fcm-white: #ffffff;
  --fcm-grey: #505050;
  --fcm-black: #000000;

  min-height: 100vh;
  background: var(--fcm-white);
  color: var(--fcm-grey);
  font-family: "Inter", "Segoe UI", sans-serif;
}

/* Hero */
.travel-hero {
  background:
    radial-gradient(ellipse at top right, rgba(0, 222, 0, 0.22), transparent 55%),
    linear-gradient(135deg, #000000 0%, #0a0a0a 60%, #111111 100%);
  padding: 150px 0 90px;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.travel-hero::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 4px;
  background: var(--fcm-green);
}
.travel-hero-inner {
  max-width: 820px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 1;
}
.travel-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: var(--fcm-white);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  border: 1px solid rgba(0, 222, 0, 0.5);
  background: rgba(0, 222, 0, 0.08);
  padding: 7px 18px;
  border-radius: 50px;
  margin-bottom: 24px;
}
.travel-eyebrow .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--fcm-green);
  box-shadow: 0 0 0 0 rgba(0, 222, 0, 0.6);
  animation: pulseDot 2s infinite;
}
@keyframes pulseDot {
  0% { box-shadow: 0 0 0 0 rgba(0, 222, 0, 0.6); }
  70% { box-shadow: 0 0 0 10px rgba(0, 222, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 222, 0, 0); }
}
.travel-title {
  font-size: clamp(2.2rem, 5vw, 3.6rem);
  font-weight: 800;
  color: var(--fcm-white);
  line-height: 1.1;
  margin-bottom: 18px;
}
.travel-title .accent {
  color: var(--fcm-green);
}
.travel-lede {
  font-size: clamp(1rem, 1.4vw, 1.15rem);
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.7;
  max-width: 640px;
  margin: 0 auto;
}
.travel-lede sup { font-size: 0.6em; }

/* Content */
.travel-content {
  padding: 70px 0 100px;
}
.travel-wrap {
  max-width: 980px;
  margin: 0 auto;
  padding: 0 24px;
}

.travel-card {
  background: var(--fcm-white);
  border: 1px solid #e6e6e6;
  border-radius: 16px;
  padding: 30px 32px;
  margin-bottom: 22px;
  box-shadow: 0 6px 26px rgba(0, 0, 0, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}
.travel-card:hover {
  transform: translateY(-4px);
  border-color: rgba(0, 222, 0, 0.55);
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.1);
}

.card-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: rgba(0, 222, 0, 0.12);
  color: #00a800;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  margin-bottom: 16px;
}

.card-title {
  font-size: clamp(1.4rem, 2.4vw, 1.8rem);
  font-weight: 800;
  color: var(--fcm-black);
  margin-bottom: 14px;
}
.card-subtitle {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--fcm-black);
  margin-bottom: 10px;
}
.card-text {
  font-size: 0.98rem;
  line-height: 1.7;
  color: var(--fcm-grey);
  margin: 0;
}
.card-text strong { color: var(--fcm-black); }
.card-note {
  font-size: 0.82rem;
  color: #8a8a8a;
  margin: 14px 0 0;
  letter-spacing: 0.02em;
}

/* Partner card */
.partner-card {
  border: 2px solid var(--fcm-green);
  background: linear-gradient(180deg, #ffffff 0%, #f3fff3 100%);
  text-align: center;
}
.partner-badge {
  display: inline-block;
  background: var(--fcm-green);
  color: var(--fcm-black);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 6px 16px;
  border-radius: 50px;
  margin-bottom: 16px;
}
.partner-card .card-text {
  max-width: 580px;
  margin: 0 auto 22px;
}

/* Grid */
.travel-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
  margin-bottom: 22px;
}
.travel-grid .travel-card { margin-bottom: 0; }

/* Buttons */
.travel-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--fcm-green);
  color: var(--fcm-black);
  font-weight: 800;
  font-size: 1rem;
  padding: 14px 32px;
  border-radius: 50px;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  box-shadow: 0 10px 28px rgba(0, 222, 0, 0.35);
}
.travel-btn:hover {
  transform: translateY(-2px);
  background: #00c400;
  color: var(--fcm-black);
  box-shadow: 0 16px 38px rgba(0, 222, 0, 0.5);
}

/* Practical */
.practical-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 28px;
}
.practical-list li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.96rem;
  line-height: 1.6;
  color: var(--fcm-grey);
}
.practical-list li i {
  color: #00a800;
  margin-top: 4px;
  flex-shrink: 0;
}
.practical-list strong { color: var(--fcm-black); }

/* CTA */
.travel-cta {
  text-align: center;
  margin-top: 36px;
  padding: 54px 40px;
  background: var(--fcm-black);
  border-radius: 22px;
  border: 1px solid rgba(0, 222, 0, 0.4);
}
.cta-title {
  font-size: clamp(1.5rem, 3vw, 2.1rem);
  font-weight: 800;
  color: var(--fcm-white);
  margin-bottom: 10px;
}
.cta-subtitle {
  color: rgba(255, 255, 255, 0.72);
  max-width: 460px;
  margin: 0 auto 26px;
}
.travel-cta .cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--fcm-green);
  color: var(--fcm-black);
  font-weight: 800;
  padding: 13px 32px;
  border-radius: 50px;
  text-decoration: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 8px 30px rgba(0, 222, 0, 0.35);
}
.travel-cta .cta-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 40px rgba(0, 222, 0, 0.55);
  color: var(--fcm-black);
}

@media (max-width: 768px) {
  .travel-hero { padding: 120px 0 70px; }
  .travel-grid { grid-template-columns: 1fr; }
  .practical-list { grid-template-columns: 1fr; }
  .travel-card { padding: 24px 22px; }
  .travel-cta { padding: 40px 24px; }
}
</style>
